import {Col, Divider, Row, Spin, Statistic, Typography} from 'antd';
import {IncreaseTag} from 'components/IncreaseTag';
import {colors} from 'lib/colors';
import {darken} from 'polished';
import React from 'react';
import {
  Legend,
  LegendProps,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis
} from 'recharts';
import {AxisInterval} from 'recharts/src/util/types';

type DataRow = {
  timestamp: number;
  first?: number;
  second?: number;
};

type Props = {
  loading?: boolean;
  height?: number;
  data: DataRow[];
  dataLabel: string;
  minTickGap?: number;
  syncId?: string;
  negative?: boolean;
  showLine?: boolean;
  timestampLabel?: string;
  formatValue: (value: number) => any;
  formatTimestamp: (value: number) => any;
  interval?: AxisInterval;
  fullLegend?: boolean;
  total: {
    first: number;
    second: number;
    [key: string]: number;
  };
};

const {Text} = Typography;

export const ReportChart: React.FC<Props> = ({
  loading,
  height = 220,
  minTickGap = 250,
  data,
  dataLabel,
  negative,
  showLine,
  formatValue,
  formatTimestamp,
  interval = 'preserveStartEnd',
  total,
  fullLegend,
  syncId,
  timestampLabel = 'Previous period'
}) => {
  let lastPoint: DataRow | undefined = undefined;

  if (data.length > 1 && showLine) {
    const lastPointIndex = data.findIndex((point) => point.first === undefined && point.second);
    lastPoint = data[lastPointIndex - 1];
  }

  const tooltipFormatter = (value: number, label: string, data: any) => {
    if (data.dataKey === 'first') {
      return (
        <Text>
          {formatValue(value)}{' '}
          <IncreaseTag
            reverse={negative}
            style={{position: 'relative', top: -1, marginLeft: 5}}
            oldValue={data.payload.second}
            newValue={data.payload.first}
          />
        </Text>
      );
    }
    return formatValue(value);
  };

  const renderLegend = ({payload}: LegendProps) => {
    if (!payload) return null;
    return (
      <Row gutter={60} style={{marginBottom: 10}}>
        {payload.map((entry: any, index) => {
          if (index > 0 && !fullLegend) return null;
          return (
            <React.Fragment key={`item-${index}`}>
              {index > 0 && <Divider type="vertical" style={{height: 'auto'}} />}
              <Col>
                <Statistic
                  title={
                    <Text>
                      {entry.value}
                      {index === 0 && (
                        <IncreaseTag
                          reverse={negative}
                          style={{marginLeft: 5}}
                          oldValue={lastPoint ? lastPoint.second! : total.second}
                          newValue={total.first}
                        />
                      )}
                    </Text>
                  }
                  valueStyle={{color: entry.color}}
                  value={formatValue(total[entry.dataKey])}
                />
              </Col>
            </React.Fragment>
          );
        })}
      </Row>
    );
  };

  return (
    <Spin spinning={loading} style={{height: '100%'}}>
      <ResponsiveContainer height={height} width="100%">
        <LineChart data={data} syncId={syncId}>
          <Legend chartHeight={height} verticalAlign="top" content={renderLegend as any} />
          <Tooltip
            labelFormatter={(value) => formatTimestamp(Number.parseInt(value as string))}
            contentStyle={{
              fontSize: 13,
              padding: '4px 6px',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              borderColor: '#e8e8e8'
            }}
            itemStyle={{padding: 0}}
            formatter={tooltipFormatter}
          />
          <Line
            type="linear"
            dataKey="first"
            stroke={colors.primary}
            isAnimationActive={false}
            name={dataLabel}
            dot={false}
            strokeWidth={1.5}
          />
          <Line
            type="linear"
            dataKey="second"
            stroke="#B8BDC3"
            isAnimationActive={false}
            name={timestampLabel}
            dot={false}
            strokeWidth={1}
          />
          <XAxis
            dataKey="timestamp"
            minTickGap={minTickGap}
            stroke={darken(0.25)(colors.darkGrey)}
            interval={interval}
            tickFormatter={(value) => formatTimestamp(Number.parseInt(value))}
          />
          {lastPoint && <ReferenceLine x={lastPoint.timestamp} strokeDasharray="3 3" />}
          {lastPoint && (
            <ReferenceDot
              x={lastPoint.timestamp}
              y={lastPoint.first}
              r={3}
              fill={colors.primary}
              stroke="none"
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </Spin>
  );
};
